import { Controller } from "@hotwired/stimulus"
import { ensureAnnouncer, announce } from "../../../lib/work/announce"

// Work kit selection engine (W3 Track C) -- stacked with the backlog engine
// and the drag kernel on one root:
//
//   data-controller="ui--work-backlog ui--work-selection ui--work-drag"
//
// Owns ALL selection state (ordered id array + anchor) and the bulk bar. The
// backlog engine's keyboard map adds no selection state of its own: Space
// bridges through ui--work:selection-request {issue_id, mode: "toggle"} --
// the exact seam the W11 navigator's stub contract reserves, so the navigator
// swap replaces nothing here. Pointer semantics on rows (delegated on the
// root, so host re-renders never rewire):
//
//   click on row checkbox  -> additive toggle (anchor moves on check)
//   click + shift          -> inclusive DOM-order range from the anchor
//                             (across sprint boundaries)
//   click + meta/ctrl      -> additive toggle, anchor moves
//   plain click            -> single select
//
// Every change emits ONE event and mutates nothing else (events are the only
// API -- anti-idea 2):
//
//   ui--work:selection-change { ids[], count, anchor_id }
//   ui--work:bulk-action      { action_id, issue_ids[] }
//
// Disabled bulk actions (host permission flags, rendered disabled) never
// emit. The bulk bar's visibility and count copy follow the selection size;
// the localized one/other templates ride the bar's data attributes so the
// pluralization stays server-side i18n.

const SELECTION_CHANGE_EVENT = "ui--work:selection-change"
const SELECTION_REQUEST_EVENT = "ui--work:selection-request"
const BULK_ACTION_EVENT = "ui--work:bulk-action"

const DEFAULT_ANNOUNCEMENTS = {
  selected: "{count} work items selected",
  selection_cleared: "Selection cleared",
}

export default class WorkSelectionController extends Controller {
  // Initial selection as a JSON Array string (the W11 navigator's swap
  // contract: selected_ids reaches the engine as hydration data). Rows
  // carrying data-selected="true" are also honored; both sources union in
  // DOM order. Emitted only when the host seeds a selection.
  static values = {
    selectedIds: String,
    announcements: String,
  }

  // The bulk bar rendered inside the root (Ui::Work::BulkBarComponent).
  // Optional: surfaces without a bar still select and emit.
  static targets = ["bar", "count"]

  connect() {
    if (this._bound) return // Turbo restoration: idempotent connect
    this._bound = true

    this._ids = []
    this._anchorId = null

    this._announcements = { ...DEFAULT_ANNOUNCEMENTS }
    if (this.hasAnnouncementsValue && this.announcementsValue !== "") {
      try {
        Object.assign(this._announcements, JSON.parse(this.announcementsValue))
      } catch {
        console.error("[ui--work-selection] announcements value is not valid JSON:", this.announcementsValue)
      }
    }

    this._onClick = (event) => this._onRootClick(event)
    this._onRequest = (event) => this._onSelectionRequest(event)
    this._announcer = ensureAnnouncer(this.element)

    this.element.addEventListener("click", this._onClick)
    this.element.addEventListener(SELECTION_REQUEST_EVENT, this._onRequest)

    this._seed()
    this._commit({ announce: false })
  }

  disconnect() {
    if (!this._bound) return
    this._bound = false
    this.element.removeEventListener("click", this._onClick)
    this.element.removeEventListener(SELECTION_REQUEST_EVENT, this._onRequest)
  }

  // -- Public API (also the W11 navigator seam) ------------------------------

  ids() {
    return [...this._ids]
  }

  // Additive toggle. moveAnchor: Space/plain-toggle moves the anchor; the
  // navigator's x-mode bulk toggle does not.
  toggle(id, { moveAnchor = true } = {}) {
    if (typeof id !== "string" || id === "") return
    const index = this._ids.indexOf(id)
    if (index === -1) {
      this._ids.push(id)
      this._anchorId = moveAnchor ? id : this._anchorId
    } else {
      this._ids.splice(index, 1)
      if (moveAnchor && this._anchorId === id) this._anchorId = null
    }
    this._commit()
  }

  setSingle(id) {
    if (typeof id !== "string" || id === "") return
    this._ids = [id]
    this._anchorId = id
    this._commit()
  }

  // Inclusive DOM-order range from the anchor to `toId` -- across sprint
  // boundaries (rows of the whole surface, in surface order). Without an
  // anchor this degrades to a single select.
  setRange(toId) {
    if (typeof toId !== "string" || toId === "") return
    const rows = this._rows()
    const anchorIndex = this._anchorId ? rows.findIndex((row) => row.getAttribute("data-work-row-id") === this._anchorId) : -1
    const toIndex = rows.findIndex((row) => row.getAttribute("data-work-row-id") === toId)
    if (anchorIndex === -1 || toIndex === -1) {
      this.setSingle(toId)
      return
    }
    const [start, end] = anchorIndex <= toIndex ? [anchorIndex, toIndex] : [toIndex, anchorIndex]
    this._ids = rows
      .slice(start, end + 1)
      .map((row) => row.getAttribute("data-work-row-id"))
    this._commit()
  }

  clear() {
    if (!this._ids.length && !this._anchorId) return
    this._ids = []
    this._anchorId = null
    this._commit()
  }

  // -- Event handlers --------------------------------------------------------

  _onRootClick(event) {
    const bulk = event.target.closest("[data-work-bulk-action-id]")
    if (bulk) {
      this._onBulkAction(bulk)
      return
    }
    const row = event.target.closest("[data-work-row-id]")
    if (!row) return
    const id = row.getAttribute("data-work-row-id")
    if (!id) return

    const checkbox = event.target.closest("input[data-work-row-checkbox]")
    if (checkbox) {
      this.toggle(id)
      return
    }
    if (event.shiftKey) {
      this.setRange(id)
      return
    }
    if (event.metaKey || event.ctrlKey) {
      this.toggle(id)
      return
    }
    this.setSingle(id)
  }

  _onBulkAction(actionEl) {
    if (actionEl.disabled || actionEl.getAttribute("aria-disabled") === "true") return
    const actionId = actionEl.getAttribute("data-work-bulk-action-id")
    if (!actionId) return
    this._emit(BULK_ACTION_EVENT, { action_id: actionId, issue_ids: [...this._ids] })
  }

  // The engine-agnostic seam: the backlog keyboard map and the W11 navigator
  // both dispatch selection-request; this controller applies the intent.
  // mode "toggle" moves the anchor, "bulk-toggle" does not, "clear" empties.
  _onSelectionRequest(event) {
    const detail = event.detail
    if (!detail) return
    if (detail.mode === "clear") {
      this.clear()
      return
    }
    if (detail.mode === "bulk-toggle") {
      this.toggle(detail.issue_id, { moveAnchor: false })
      return
    }
    if (detail.mode === "toggle") {
      this.toggle(detail.issue_id)
    }
  }

  // -- State application -----------------------------------------------------

  // Hydration seed: the selected-ids value plus any SSR-selected rows
  // (data-selected="true"), unioned in DOM order, then value-only ids
  // appended (rows the host did not render).
  _seed() {
    const seeded = new Set()
    for (const row of this._rows()) {
      if (row.getAttribute("data-selected") === "true") seeded.add(row.getAttribute("data-work-row-id"))
    }
    if (this.hasSelectedIdsValue && this.selectedIdsValue !== "") {
      try {
        const parsed = JSON.parse(this.selectedIdsValue)
        if (Array.isArray(parsed)) parsed.forEach((id) => typeof id === "string" && id !== "" && seeded.add(id))
      } catch {
        console.error("[ui--work-selection] selected-ids value is not valid JSON:", this.selectedIdsValue)
      }
    }
    this._ids = [...seeded]
    this._anchorId = this._ids.length ? this._ids[0] : null
  }

  _commit({ announce: withAnnounce = true } = {}) {
    for (const row of this._rows()) {
      const selected = this._ids.includes(row.getAttribute("data-work-row-id"))
      row.setAttribute("aria-selected", String(selected))
      row.setAttribute("data-selected", String(selected))
      row.querySelectorAll("[data-selected]").forEach((el) => el.setAttribute("data-selected", String(selected)))
      const checkbox = row.querySelector("input[data-work-row-checkbox]")
      if (checkbox) checkbox.checked = selected
    }
    this._syncBar()
    this._emit(SELECTION_CHANGE_EVENT, {
      ids: [...this._ids],
      count: this._ids.length,
      anchor_id: this._anchorId,
    })
    if (withAnnounce) {
      if (this._ids.length) {
        announce(this._announcer, this._announcements.selected, { count: String(this._ids.length) })
      } else {
        announce(this._announcer, this._announcements.selection_cleared)
      }
    }
  }

  _syncBar() {
    if (!this.hasBarTarget) return
    this.barTargets.forEach((bar) => {
      bar.hidden = this._ids.length === 0
    })
    if (!this.hasCountTarget) return
    const count = this._ids.length
    this.countTargets.forEach((countEl) => {
      const bar = countEl.closest("[data-work-bulk-bar]")
      const template = bar?.getAttribute("data-work-bulk-count")
      countEl.textContent = template
        ? template.replace(/%\{count\}/g, String(count))
        : String(count)
    })
  }

  _rows() {
    return [...this.element.querySelectorAll("[data-work-row-id]")]
  }

  _emit(name, detail) {
    this.element.dispatchEvent(new CustomEvent(name, { bubbles: true, cancelable: true, detail }))
  }
}
